import { Textarea } from '@hakimo-ui/shared/ui-base';
import { History } from 'lucide-react';
import { useState } from 'react';
import { EditActions, EditButton, Panel } from '../Panel';

interface Props {
  title: string;
  value: string;
  originalValue?: string;
  placeholder?: string;
  saving?: boolean;
  onSave: (next: string, onSaved: () => void) => void;
  rows?: number;
}

const LABEL_CLASS =
  'text-onlight-text-2 dark:text-ondark-text-2 text-xs font-semibold uppercase tracking-wide';

export function EditableCard({
  title,
  value,
  originalValue = '',
  placeholder,
  saving = false,
  onSave,
  rows = 3,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const hasPrevious = !!originalValue && originalValue !== value;
  const previousShown = hasPrevious && showPrevious;

  const startEdit = () => {
    setDraft(value ?? '');
    setEditing(true);
  };

  // Closes on success only, or a failed save loses the operator's text
  // to the refetch that follows.
  const save = () => onSave((draft ?? '').trim(), () => setEditing(false));

  return (
    <Panel
      title={title}
      titleAdornment={
        hasPrevious && (
          <button
            type="button"
            onClick={() => setShowPrevious((prev) => !prev)}
            aria-pressed={showPrevious}
            aria-label={`${showPrevious ? 'Hide' : 'View'} logged ${title}`}
            className={`rounded p-1 ${
              showPrevious
                ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-1 dark:text-ondark-text-1'
                : 'text-onlight-text-2 dark:text-ondark-text-2 dark:hover:bg-ondark-bg-3 hover:bg-onlight-bg-2'
            }`}
          >
            <History className="h-4 w-4" />
          </button>
        )
      }
      action={!editing && <EditButton label={title} onClick={startEdit} />}
    >
      {previousShown && (
        <div className="mt-2">
          <div className={LABEL_CLASS}>Logged</div>
          <p className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 whitespace-pre-wrap text-sm">
            {originalValue}
          </p>
          <hr className="dark:border-ondark-line-2 border-onlight-line-2 mt-3" />
          <div className={`${LABEL_CLASS} mt-3`}>
            {editing ? 'Draft' : 'Edited'}
          </div>
        </div>
      )}

      {editing ? (
        <div className="mt-2">
          <Textarea
            value={draft}
            onChange={setDraft}
            rows={rows}
            placeholder={placeholder}
          />
          <EditActions
            saving={saving}
            onSave={save}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <div className="text-onlight-text-1 dark:text-ondark-text-1 mt-2 whitespace-pre-wrap text-sm">
          {value || '-'}
        </div>
      )}
    </Panel>
  );
}

export default EditableCard;
